<template lang="pug">
  #map
    div(map, ref='map' style='width: 100%;')
      div.side-icon-container.camadas
        .side-icon(
          @click='showMapa = !showMapa',
          title='Visualizar camadas'
        )
          span.mdi.mdi-layers
      div.side-icon-container.focus
        .side-icon(
          @click='focusOnCenter',
          title='Centralizar'
        )
          div
            span.mdi.mdi-target
    transition(name='slide', ref='slide', v-if='mapaInfo && grupos.length > 0')
      el-col(:md='24' :lg='14' :xl='12', style='overflow-y: scroll; min-width: 320px; padding: 0px;', v-if='showMapa')
        .group-layer
          CollectionGroupLayer(
            :groups='grupos'
            @layer-toggle='toggleLayers'
          )
</template>

<script>
import Wicket from 'wicket';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-editable';
import { buscarInfoMapa } from '@/services/infoMapa';
import EtapaEnum from '@/enums/EtapaEnum';

import mapaInfo from '@/utils/mapaInfo';
import TipoDocumentoImovelEnum from '@/enums/TipoDocumentoImovelEnum';
import * as esri from 'esri-leaflet';
import * as turf from '@turf/turf';
import { deepCopy } from '@/utils/utils';
import CollectionGroupLayer from './CollectionGroupLayer.vue';
import { TipoAreaEnum } from '@/enums/tipo-area.enum.js';

const SOUTH_WEST = L.latLng(-89.98155760646617, -180);
const NORTH_EAST = L.latLng(89.99346179538875, 180);
const GEOJSON_PRECISION = 32;

export const OverlapLevel = {
    ERROR: 'ERROR',
    WARNING: 'WARNING',
    NONE: 'NONE',
};

export const LayerEventError = {
    INVALID: 'invalid',
    INTERSECTS: 'intersects',
    OUTSIDE_BOUNDARIES: 'outside-boundaries',
    INVALID_FILE: 'invalid-file',
    EMPTY_FILE: 'empty-file',
};

const Colors = {
    SUCCESS: '#3388ff',
    ERROR: '#F00F00',
    WARNING: '#F0EC00',
    HIGHLIGHTED: '#6F9ED0',
    INTERSECTS: '#F00F00',
};

const Mode = {
    DRAWING: 'drawing',
    EDITING: 'editing',
    EDITED: 'edited',
    REMOVED: 'removed',
    INVALID: 'invalid',
};

const MapEventError = {
    DRAWING: 'drawing:error',
    UPLOAD: 'upload:error',
};

const UploadableFileTypes = {
    KML: 'kml',
    SHAPE: 'zip',
};

const DefaultOptions = {
    showDrawingArea: true,
    fullscreen: true,
    isEditable: true,
    overlapLevel: OverlapLevel.ERROR,
    isLayerUploadable: true,
    mapLayersList: [
        {
            name: 'Satélite',
            map: 'Imagery',
            labels: 'ImageryLabels',
        },
        {
            name: 'Terreno',
            map: 'Topographic',
        },
    ],
    drawingBoundaries: {
        remoteMethod: null,
        style: {
            sliced: {
                fill: true,
                fillColor: '#00A6FF',
                fillOpacity: 0.1,
                color: '#00A6FF',
                opacity: 1,
                weight: 2,
            },
        },
        title: 'Mostrar limites para georreferenciamento',
    },
    basemapLayer: {
        map: 'Imagery',
        labels: 'ImageryLabels',
    },
};

export default {
    name: 'Mapa',
    components: {
        CollectionGroupLayer,
    },
    props: {
        codigoImovel: {
            type: String,
            required: false,
        },
        itemMenu: {
            type: Object,
            required: false,
        },
        groups: {
            type: Array,
            required: false,
        },
        modulosFiscais: {
            type: Number,
            required: false,
        },
        pagina: {
            type: String,
            default: '',
        },
        area: {
            type: String,
        },
    },
    data() {
        return {
            showMapa: false,
            mapaInfo,
            usuario: null,
            layers: {
                map: null,
                labels: null,
                area: {
                    imovel: null,
                    pane: null,
                    group: null,
                    drawable: [],
                },
                intersection: {
                    group: null,
                    pane: null,
                    drawings: [],
                },
            },
            drawing: {
                active: {
                    mode: null,
                    layer: null,
                    originalLatLng: null,
                },
                boundaries: {
                    group: null,
                    geometries: [],
                },
                group: null,
                pane: null,
                name: '',
            },
            grupos: [],
            mapOptions: {},
            defaultCenterBounds: null,
            options: {
                isEditable: true,
                overlapLevel: OverlapLevel.NONE,
                showDrawingArea: true,
            },
            geometryFunction: L.polygon,
            wkt: new Wicket.Wkt(),
            layerAtual: null,
            notifications: [],
            areaTotalVetorizada: null,
            areaInvalidaPropPosse: null,
            areaInvalidaRLM: null,
            grupoRL: null,
            variavelNumber: null,
        };
    },
    watch: {
        layerAtual(newValue) {
            this.$emit('layerAtualUpdated', newValue);
        },
        itemMenu(newValue) {
            if (newValue && JSON.stringify(newValue) !== '{}') {
                this.editarVetorizacao(newValue);
            }
        },
    },

    async mounted() {
        try {
            this.mapaInfo = await buscarInfoMapa(this.area, this.codigoImovel);

            this.mapOptions = deepCopy(DefaultOptions, this.options);
            const { basemapLayer } = this.mapOptions;

            if (!basemapLayer || !basemapLayer.map) {
                throw new Error('Sem basemap layer.');
            }
        } catch (error) {
            console.error('Erro ao carregar mapa:', error);
        }

        this.mapOptions = deepCopy(DefaultOptions, this.options);
        const { basemapLayer } = this.mapOptions;
        if (!basemapLayer?.map) {
            throw new Error('Sem basemap layer.');
        }

        const mapRef = this.$refs.map;
        this.map = L.map(mapRef, {
            maxBounds: L.latLngBounds(SOUTH_WEST, NORTH_EAST),
            zoom: 15,
            minZoom: 7,
            doubleClickZoom: false,
            zoomControl: false,
            attributionControl: true,
            editable: true,
            editOptions: {},
        });

        this.layers.map = esri.basemapLayer(basemapLayer.map).addTo(this.map);
        if (basemapLayer.labels) {
            this.layers.labels = esri.basemapLayer(basemapLayer.labels).addTo(this.map);
        }

        L.control
            .zoom({
                position: 'topright',
                zoomInTitle: 'Aumentar zoom',
                zoomOutTitle: 'Diminuir zoom',
            })
            .addTo(this.map);

        this.drawing.pane = this.map.createPane('drawingPane');
        this.drawing.pane.style.zIndex = 450;
        this.drawing.group = L.featureGroup([], {
            pane: 'drawingPane',
        }).addTo(this.map);

        this.layers.area.pane = this.map.createPane('areasPane');
        this.layers.area.pane.style.zIndex = 400;
        this.layers.area.group = L.featureGroup([], {
            pane: 'areasPane',
        }).addTo(this.map);

        this.layers.intersection.pane = this.map.createPane('intersectionPane');
        this.layers.intersection.pane.style.zIndex = 425;
        this.layers.intersection.group = L.featureGroup([], {
            pane: 'intersectionPane',
        }).addTo(this.map);

        this.adicionarCamadasMapa(this.mapaInfo.grupos);

        this.grupoRL = this.mapaInfo.grupos.filter((grupo) => grupo.id === 38);

        this.map.doubleClickZoom.disable();

        this.addListeners();

        const that = this;
        this.$nextTick(() => {
            const bounds = that.layers.area.group.getBounds();
            that.setDefaultCenter(bounds, true);
            that.map.invalidateSize();
        });

        if (this.pagina === EtapaEnum.GEO_RVN.codigo) {
            this.verificarAreaInvalidaRLM();
        }
        this.$emit('update-loading', false);

        setTimeout(() => {
            this.$emit('loaded');
        }, 1);
    },
    methods: {
        adicionarCamadasMapa(grupos) {
            for (const grupo of grupos) {
                if (!grupo.hasGeo) {
                    continue;
                }
                const areas = [];
                for (let index = 0; index < grupo.areas.length; index++) {
                    let area = grupo.areas[index];
                    if (!area.geo) {
                        continue;
                    }

                    const isAreaImovel = area.estilo?.color;

                    areas.push(area);

                    const geoJSON = JSON.parse(area.geo);
                    let layer = L.geoJSON(geoJSON);
                    if (isAreaImovel) {
                        layer.setStyle(area.estilo);
                    } else {
                        layer.setStyle({ color: area.color });
                        geoJSON.id = area.id;
                        geoJSON.tipo = grupo.tipo;
                        this.layers.area.drawable.push(geoJSON);
                    }

                    layer.properties = {
                        id: area.id,
                        isHidden: false,
                        strokeOpacity: area?.estilo?.opacity ?? 1.0,
                        fillOpacity: area?.estilo?.fillOpacity ?? 0.3,
                    };
                    this.layers.area.group.addLayer(layer);
                    if (area.codigo === 'AREA_IMOVEL') {
                        this.layers.area.imovel = layer.toGeoJSON(GEOJSON_PRECISION);
                    }
                }
                if (areas.length > 0) {
                    this.grupos.push({
                        ...grupo,
                        areas,
                    });
                }
            }
        },
        addListeners() {
            const self = this;

            const checkIfGeometryValid = (layer) => {
                const latlngs = layer.getLatLngs();
                return latlngs.length === 1 && latlngs[0].length >= 3;
            };

            const getTempLayer = (layer, { backwardLineGuide, forwardLineGuide } = {}) => {
                const latlngs = self._getLayerLatLng(layer);

                const latLngBackward = backwardLineGuide?.getLatLngs();
                const latLngForward = forwardLineGuide?.getLatLngs();
                if (!latLngBackward || latLngBackward.length === 0 || !latLngForward || latLngForward.length === 0) {
                    return self.geometryFunction(latlngs).addTo(this.map);
                }

                let latLngDrawing = null;
                for (let i = 0; i < 2; i += 1) {
                    if (
                        latLngBackward[i].lat === latLngForward[i].lat &&
                        latLngBackward[i].lng === latLngForward[i].lng
                    ) {
                        latLngDrawing = latLngBackward[i];
                        break;
                    }
                }

                if (!latLngDrawing) {
                    return this.geometryFunction(latlngs).addTo(this.map);
                }
                return this.geometryFunction([...latlngs, latLngDrawing]).addTo(this.map);
            };

            const isDrawingValid = (layer, { backwardLineGuide, forwardLineGuide }) => {
                const tempLayer = getTempLayer(layer, {
                    backwardLineGuide,
                    forwardLineGuide,
                });
                const error = self._getLayerError(tempLayer);

                self._paintOverlapedLayers(layer);

                if (error.fail !== null) {
                    self.setLayerColor(layer, Colors.ERROR);
                } else if (error.warning !== null) {
                    self._notify(MapEventError.DRAWING, {
                        id: layer._leaflet_id,
                        layer,
                        error: error.warning,
                    });
                } else {
                    self.setLayerColor(layer, Colors.SUCCESS);
                }
                tempLayer.remove();

                return error.fail === null;
            };

            this.map.on('editable:vertex:click', ({ cancel, editTools, latlng, layer }) => {
                const latlngs = self._getLayerLatLng(layer);
                let tempLayer = null;

                const isEditing = self.drawing.active.mode === Mode.EDITING;
                const minPoints = 3;
                if (self.drawing.active.mode === Mode.DRAWING && latlngs.length >= minPoints) {
                    tempLayer = getTempLayer(layer);
                }

                if (isEditing && latlngs.length - 1 >= minPoints) {
                    tempLayer = self.geometryFunction(latlngs.filter((ll) => ll !== latlng)).addTo(self.map);
                }

                if (tempLayer !== null) {
                    const error = self._getLayerError(tempLayer);
                    self._paintOverlapedLayers(layer);
                    if (error.fail !== null && error.fail != 'outside-boundaries') {
                        if (!isEditing) {
                            self.setLayerColor(layer, Colors.ERROR);
                        }
                        self._notify(MapEventError.DRAWING, {
                            id: layer._leaflet_id,
                            layer,
                            error: error.fail,
                        });
                        cancel();
                    } else if (error.warning !== null) {
                        self._notify(MapEventError.DRAWING, {
                            id: layer._leaflet_id,
                            layer,
                            error: error.warning,
                        });
                    } else {
                        self.setLayerColor(layer, Colors.SUCCESS);
                    }
                    tempLayer.remove();
                }
            });

            this.map.on('editable:vertex:click', ({ cancel, editTools, latlng, layer }) => {
                const latlngs = self._getLayerLatLng(layer);
                let tempLayer = null;

                const isEditing = self.drawing.active.mode === Mode.EDITING;
                const minPoints = self.drawType === 'POLYGON' ? 3 : 2;
                if (self.drawing.active.mode === Mode.DRAWING && latlngs.length >= minPoints) {
                    tempLayer = getTempLayer(layer);
                }

                if (isEditing && latlngs.length - 1 >= minPoints) {
                    tempLayer = self.geometryFunction(latlngs.filter((ll) => ll !== latlng)).addTo(self.map);
                }

                if (tempLayer !== null) {
                    const error = self._getLayerError(tempLayer);
                    self._paintOverlapedLayers(layer);
                    if (error.fail !== null && error.fail != 'outside-boundaries') {
                        if (!isEditing) {
                            self.setLayerColor(layer, Colors.ERROR);
                        }
                        _(MapEventError.DRAWING, {
                            id: layer._leaflet_id,
                            layer,
                            error: error.fail,
                        });
                        cancel();
                    } else if (error.warning !== null) {
                        if (!isEditing) {
                            self.setLayerColor(editTools.backwardLineGuide, Colors.DrawingLine.WARNING);
                            self.setLayerColor(editTools.forwardLineGuide, Colors.DrawingLine.WARNING);
                        }
                        _(MapEventError.DRAWING, {
                            id: layer._leaflet_id,
                            layer,
                            error: error.warning,
                        });
                    } else {
                        self.setLayerColor(layer, Colors.SUCCESS);
                    }
                    tempLayer.remove();
                }
            });

            this.map.on('editable:drawing:move', ({ layer, editTools }) => {
                if (checkIfGeometryValid(layer) && isDrawingValid(layer, editTools)) {
                    const tempLayer = getTempLayer(layer, editTools);
                    self.calculateDrawingArea();
                    tempLayer.remove();
                }
            });

            this.map.on('editable:drawing:commit', async ({ layer }) => {
                layer.disableEdit();
                self.map.removeLayer(layer);

                this.finalizarDesenhoLayer(layer);
            });
        },
        finalizarDesenhoLayer(layer) {
            this.drawing.active.mode = null;
            this.drawing.active.layer = null;
            this.drawing.active.originalLatLng = null;

            layer = this.verificarLimiteImovel(layer);
            this.verificarAreaInvalidaPropPosse();

            layer.options.pane = 'drawingPane';
            layer.setStyle({ fillOpacity: 0.3 });

            this.drawing.group.addLayer(layer);

            this._paintOverlapedLayers();

            let newLayer = this._paintIntersections(layer);
            if (newLayer) {
                newLayer = newLayer.getLayers()[0];
                this.drawing.group.removeLayer(layer);
                if (newLayer.feature.geometry.type === 'MultiPolygon') {
                    const firstPolygonCoordinates = newLayer.feature.geometry.coordinates[0];

                    const polygonGeometry = {
                        type: 'Polygon',
                        coordinates: firstPolygonCoordinates,
                    };
                    const layers = L.geoJSON(polygonGeometry);
                    newLayer = layers.getLayers()[0];
                }
                layer = newLayer;
                this.drawing.group.addLayer(layer);
            }

            this.calculateDrawingArea();

            if (this.mapOptions.isEditable) {
                layer.on('click', () => this._layerOnClick(layer));
                layer.on('dblclick', () => this._layerOnDoubleClick(layer));
            }
        },
        verificarLimiteImovel(layer) {
            let { geometry } = layer.toGeoJSON(GEOJSON_PRECISION);
            const imovelGeometry = this.layers.area.imovel.features[0].geometry;

            const intersection = turf.intersect(imovelGeometry, geometry);
            if (intersection) {
                const geoJson = L.geoJSON(intersection);
                layer = geoJson.getLayers()[0];
            }
            return layer;
        },
        verificarAreaInvalidaPropPosse() {
            let area = this.layers.area.imovel.features[0].geometry;
            for (const geometry of this.layers.area.drawable) {
                if (
                    geometry.tipo === TipoDocumentoImovelEnum.PROPRIEDADE.id ||
                    geometry.tipo === TipoDocumentoImovelEnum.RL_PROPOSTA.id
                ) {
                    area = turf.difference(area, geometry);
                    this.areaInvalidaPropPosse = area;
                }
            }
        },
        verificarAreaInvalidaRLM() {
            let area = this.layers.area.imovel.features[0].geometry;
            for (const areaRL of this.grupoRL[0].areas) {
                if (areaRL.id !== 183 && areaRL.geo) {
                    const geometry = JSON.parse(areaRL.geo);
                    area = turf.difference(area, geometry);
                    this.areaInvalidaRLM = area;
                }
            }
        },
        toggleLayers({ layersIds, show }) {
            for (const layer of this.layers.area.group.getLayers()) {
                if (layersIds.includes(layer.properties.id)) {
                    let strokeOpacity = 0;
                    let fillOpacity = 0;
                    if (show) {
                        strokeOpacity = layer.properties.strokeOpacity;
                        fillOpacity = layer.properties.fillOpacity;
                    }
                    layer.setStyle({
                        opacity: strokeOpacity,
                        fillOpacity,
                    });
                    layer.properties.isHidden = !layer.properties.isHidden;
                }
            }
        },
        focusOnCenter() {
            this.map.invalidateSize();
            if (this.defaultCenterBounds) {
                this.map.fitBounds(this.defaultCenterBounds);
            } else if (this.drawing.group.getLayers().length > 0) {
                this.map.fitBounds(this.drawing.group.getBounds());
            }
            this._paintOverlapedLayers();
        },
        setDefaultCenter(bounds, focus, geom = null) {
            this.defaultCenterBounds = geom ? geom.getBounds() : bounds;
            if (focus) {
                this.map.fitBounds(this.defaultCenterBounds);
            }
        },
        removerPoligonoDireto(coordenadas) {
            if (coordenadas && coordenadas.length > 0) {
                const latLngs = coordenadas.slice(0, 4).map((coord) => [coord[0], coord[1]]);

                const compararComMargemDeErro = (valor1, valor2, margem = 0.00001) => {
                    return Math.abs(valor1 - valor2) < margem;
                };

                this.map.eachLayer((layer) => {
                    if (layer instanceof L.Polygon) {
                        const layerCoords = layer.getLatLngs()[0];
                        if (latLngs.length !== layerCoords.length) {
                            return;
                        }

                        const coordsMatch = latLngs.every((coord, index) => {
                            const latMatch = compararComMargemDeErro(coord[0], layerCoords[index]?.lat);
                            const lngMatch = compararComMargemDeErro(coord[1], layerCoords[index]?.lng);
                            return latMatch && lngMatch;
                        });

                        if (coordsMatch) {
                            this.map.removeLayer(layer);
                            if (this.rotateMarker) {
                                this.map.removeLayer(this.rotateMarker);
                                this.rotateMarker = null;
                            }
                        } else {
                            console.log('Coordenadas não coincidem para remocao.');
                        }
                    }
                });
            } else {
                console.log('Coordenadas inválidas ou vazias para remoção.');
            }
        },
        desenharPoligonoFixado(geometry, centro) {
            if (!this.map) {
                console.error('Mapa não está inicializado!');
                return;
            }
            if (!Array.isArray(geometry) || geometry.length === 0) {
                console.error('Geometria inválida: Nenhuma coordenada encontrada.', geometry);
                return;
            }
            const latLngs = geometry
                .map((coord) => {
                    if (!Array.isArray(coord) || coord.length !== 2) {
                        console.error('Coordenada inválida:', coord);
                        return null;
                    }
                    return [coord[1], coord[0]];
                })
                .filter((coord) => coord !== null);

            if (latLngs.length < 3) {
                console.error('Geometria inválida: o polígono deve ter ao menos 3 pontos.');
                return;
            }

            const polygon = L.polygon(latLngs, {
                color: 'red',
                fillColor: 'blue',
                fillOpacity: 0.5,
            }).addTo(this.map);

            if (centro && Array.isArray(centro) && centro.length === 2) {
                this.map.setView([centro[0], centro[1]], 15);
            }

            this.map.fitBounds(polygon.getBounds());
        },
        calculateRotationAngle(polygonLayer, novoCentro) {
            const centroAtual = turf.center(polygonLayer.toGeoJSON()).geometry.coordinates;
            const centroLat = centroAtual[1];
            const centroLng = centroAtual[0];

            const deltaLng = novoCentro.lng - centroLng;
            const deltaLat = novoCentro.lat - centroLat;

            const angleRad = Math.atan2(deltaLat, deltaLng);
            const angleDeg = angleRad * (180 / Math.PI);

            return angleDeg;
        },

        removerotacao() {
            if (this.rotateMarker) {
                this.map.removeLayer(this.rotateMarker);
                this.rotateMarker = null;
            }
        },
        desenharPoligono(index, coordenadas) {
            if (this.rotateMarker) {
                this.map.removeLayer(this.rotateMarker);
                this.rotateMarker = null;
            }

            return new Promise((resolve, reject) => {
                this.geometryFunction = L.polygon;

                if (Array.isArray(coordenadas) && coordenadas.length > 0) {
                    if (this.drawing.active && typeof this.drawing.active === 'object' && this.drawing.active[index]) {
                        const item = this.drawing.active[index];

                        if (item && item.layer) {
                            item.layer.remove();
                            this.drawing.active[index] = null;
                        }
                    }

                    const polygon = L.polygon(coordenadas);
                    this.map.addLayer(polygon);

                    this.drawing.active[index] = {
                        layer: polygon,
                        coordenadas: coordenadas,
                    };

                    resolve({ layer: polygon, geometry: polygon.toGeoJSON() });
                } else {
                    if (this.drawing.active && this.drawing.active[index]) {
                        const item = this.drawing.active[index];

                        if (item && item.layer) {
                            item.layer.remove();
                            this.drawing.active[index] = null;
                        }
                    }
                }

                if (this.rotateMarker) {
                    this.map.removeLayer(this.rotateMarker);
                    this.rotateMarker = null;
                }

                if (this.drawing.active[index] && this.drawing.active[index].layer) {
                    this.drawing.active[index].layer.remove();
                }

                this.drawing.active[index] = { mode: Mode.DRAWING, layer: null };

                this.map.on('click', (e) => {
                    if (this.drawing.active[index].layer) return;

                    const startPoint = e.latlng;
                    const northOffset = 4 / 111320;
                    const eastOffset = 25 / (111320 * Math.cos(-22.9 * (Math.PI / 180)));

                    const latLngs = [
                        [startPoint.lat, startPoint.lng],
                        [startPoint.lat + northOffset, startPoint.lng],
                        [startPoint.lat + northOffset, startPoint.lng + eastOffset],
                        [startPoint.lat, startPoint.lng + eastOffset],
                        [startPoint.lat, startPoint.lng],
                    ];

                    const layer = this.geometryFunction(latLngs, {
                        color: 'red',
                    }).addTo(this.map);
                    this.drawing.active[index].layer = layer;
                    this.drawing.active[index].layer.disableEdit();

                    const centroPoligono = turf.center(layer.toGeoJSON());
                    const rotateIcon = L.icon({
                        iconUrl: require('@/assets/opcao-de-rotacao.png'),
                        iconSize: [16, 16],
                        iconAnchor: [8, 8],
                    });

                    const rotateMarker = L.marker(
                        [centroPoligono.geometry.coordinates[1], centroPoligono.geometry.coordinates[0]],
                        {
                            icon: rotateIcon,
                            draggable: true,
                        }
                    ).addTo(this.map);

                    const atualizarIcone = () => {
                        const novoCentro = turf.center(layer.toGeoJSON());
                        rotateMarker.setLatLng([
                            novoCentro.geometry.coordinates[1],
                            novoCentro.geometry.coordinates[0],
                        ]);
                    };

                    rotateMarker.on('dragstart', () => {
                        this.map.dragging.disable();
                    });

                    rotateMarker.on('dragend', () => {
                        this.map.dragging.enable();
                        validarPoligono(layer, rotateMarker, resolve, reject);
                    });

                    rotateMarker.on('drag', (event) => {
                        const novoCentro = event.target.getLatLng();
                        const anguloRotacao = this.calculateRotationAngle(layer, novoCentro) * 0.2;

                        const poligonoRotacionado = turf.transformRotate(layer.toGeoJSON(), anguloRotacao, {
                            pivot: centroPoligono,
                        });

                        const latLngsRotacionados = poligonoRotacionado.geometry.coordinates[0].map((coord) => [
                            coord[1],
                            coord[0],
                        ]);
                        layer.setLatLngs(latLngsRotacionados);

                        atualizarIcone();
                    });

                    rotateMarker.on('dragend', () => validarPoligono(layer, rotateMarker, resolve, reject));

                    setTimeout(() => {
                        if (
                            !rotateMarker.dragging ||
                            !rotateMarker.dragging._draggable ||
                            !rotateMarker.dragging._draggable._moving
                        ) {
                            validarPoligono(layer, rotateMarker, resolve, reject);
                        }
                    }, 1000);
                });

                const validarPoligono = (layer, rotateMarker, resolve, reject) => {
                    const validar = this.validarDesenho(layer);
                    if (validar) {
                        this.drawing.active[index].layer = layer;
                        const geometriaAtualizada = layer.toGeoJSON().geometry;

                        resolve({
                            layer: layer,
                            geometry: geometriaAtualizada,
                        });
                    } else {
                        this.$notify({
                            title: 'Atenção!',
                            message:
                                'O local demarcado não é permitido. Demarque as parcelas nas áreas a serem recompostas do imóvel.',
                            type: 'warning',
                        });
                        reject(new Error('Validação falhou.'));
                    }

                    this.map.removeLayer(rotateMarker);
                    this.map.off('click');
                    this.map.dragging.enable();
                };
            });
        },
        validarDesenho(layer) {
            const tipoArea = TipoAreaEnum[this.area];

            const grupo28 = this.mapaInfo.grupos.find((group) => group.id === tipoArea?.grupo);

            if (!grupo28 || !grupo28.areas || grupo28.areas.length === 0) {
                this.$notify({
                    title: 'Atenção!',
                    message: 'Não há áreas disponíveis para validação.',
                    type: 'warning',
                });
                return false;
            }

            this.map.dragging.disable();
            this.map.touchZoom.disable();
            this.map.scrollWheelZoom.disable();

            let isValidating = false;
            let validationTimeout = null;
            const rotatePolygon = (layer, angle) => {
                const geoJSON = layer.toGeoJSON();
                const centroPoligono = turf.center(geoJSON).geometry.coordinates;

                const geoRotacionado = turf.transformRotate(geoJSON, angle, {
                    pivot: [centroPoligono[0], centroPoligono[1]],
                });

                layer.setLatLngs(geoRotacionado.geometry.coordinates[0].map((coord) => [coord[1], coord[0]]));

                layer.redraw();
            };

            const revalidatePolygon = (layer) => {
                if (isValidating) return false;
                isValidating = true;

                const latLngs = layer.getLatLngs()[0];
                const areaContemPoligono = grupo28.areas.find((area) => {
                    let coordenadas = area.geo;

                    // Parse das coordenadas
                    if (typeof coordenadas === 'string') {
                        try {
                            coordenadas = JSON.parse(coordenadas);
                        } catch (e) {
                            this.$notify({
                                title: 'Erro!',
                                message: 'Falha ao interpretar coordenadas.',
                                type: 'error',
                            });
                            console.error('Erro ao parsear coordenadas:', e, area);
                            isValidating = false;
                            return false;
                        }
                    }

                    if (!coordenadas || !coordenadas.type || !coordenadas.coordinates) {
                        this.$notify({
                            title: 'Erro!',
                            message: 'A área não contém coordenadas válidas.',
                            type: 'error',
                        });
                        console.error('Área inválida após parse:', area);
                        isValidating = false;
                        return false;
                    }

                    let areaPoligonos = [];
                    if (coordenadas.type === 'MultiPolygon') {
                        areaPoligonos = coordenadas.coordinates.map((coords) => turf.polygon(coords));
                    } else if (coordenadas.type === 'Polygon') {
                        areaPoligonos = [turf.polygon(coordenadas.coordinates)];
                    } else {
                        this.$notify({
                            title: 'Erro!',
                            message: 'Geometria não suportada.',
                            type: 'error',
                        });
                        isValidating = false;
                        return false;
                    }

                    const pontoDentro = latLngs.some((pt) =>
                        areaPoligonos.some((areaPoly) => turf.booleanPointInPolygon([pt.lng, pt.lat], areaPoly))
                    );

                    if (!pontoDentro) {
                        layer.setStyle({ color: 'red' });
                        this.$notify({
                            title: 'Atenção!',
                            message: 'Polígono fora da área permitida após rotação!',
                            type: 'warning',
                        });

                        if (validationTimeout) clearTimeout(validationTimeout);

                        validationTimeout = setTimeout(() => {
                            const aindaInvalido = latLngs.every((pt) =>
                                areaPoligonos.every(
                                    (areaPoly) => !turf.booleanPointInPolygon([pt.lng, pt.lat], areaPoly)
                                )
                            );

                            if (aindaInvalido) {
                                this.map.removeLayer(layer);
                                this.$notify({
                                    title: 'Aviso!',
                                    message:
                                        'O local demarcado não é permitido. Demarque as parcelas nas áreas a serem recompostas do imóvel.',
                                    type: 'warning',
                                });

                                if (this.rotateMarker) {
                                    this.map.removeLayer(this.rotateMarker);
                                    this.rotateMarker = null;
                                }
                            }
                            isValidating = false;
                        }, 5000);

                        return false;
                    }

                    layer.setStyle({ color: 'green' });
                    isValidating = false;
                    return true;
                });

                isValidating = false;
                return areaContemPoligono !== undefined;
            };

            const atualizarIcone = () => {
                const centroPoligono = turf.center(layer.toGeoJSON());
                const iconLatLng = [centroPoligono.geometry.coordinates[1], centroPoligono.geometry.coordinates[0]];

                if (this.rotateMarker) {
                    this.rotateMarker.setLatLng(iconLatLng);
                } else {
                    const rotateIcon = L.icon({
                        iconUrl: require('@/assets/opcao-de-rotacao.png'),
                        iconSize: [20, 20],
                        iconAnchor: [10, 10],
                    });

                    this.rotateMarker = L.marker(iconLatLng, {
                        icon: rotateIcon,
                        interactive: true,
                    }).addTo(this.map);
                }
            };

            atualizarIcone();

            let isRotating = false;
            let startAngle = 0;

            this.rotateMarker.on('mousedown', (event) => {
                isRotating = true;
                startAngle = event.latlng;
            });

            this.map.on('mousemove', (event) => {
                if (isRotating) {
                    const currentAngle = event.latlng;
                    const angleDiff =
                        this.calculateRotationAngle(layer, currentAngle) -
                        this.calculateRotationAngle(layer, startAngle);
                    rotatePolygon(layer, angleDiff);
                    startAngle = currentAngle;

                    // Debounce para não validar repetidamente
                    if (validationTimeout) clearTimeout(validationTimeout);
                    validationTimeout = setTimeout(() => {
                        revalidatePolygon(layer);
                    }, 500);
                }
            });

            this.map.on('mouseup', () => {
                if (isRotating) {
                    isRotating = false;
                    revalidatePolygon(layer);

                    this.map.dragging.enable();
                    this.map.touchZoom.enable();
                    this.map.scrollWheelZoom.enable();
                }
            });

            return revalidatePolygon(layer);
        },
        isPointInValidArea(latLng) {
            const areasValidas = [this.layers.area.imovel, this.layers.area.regularizar];

            return areasValidas.some((area) => {
                if (area instanceof L.Polygon || area instanceof L.FeatureGroup) {
                    return area.getBounds().contains(latLng);
                } else if (area instanceof L.LayerGroup) {
                    return area.getLayers().some((layer) => {
                        if (layer instanceof L.Polygon) {
                            return layer.getBounds().contains(latLng);
                        }
                        return false;
                    });
                }
                return false;
            });
        },
        removerPoligono() {
            this.drawing.group.removeLayer(this.layerAtual);
            this.layerAtual = null;
            this._paintIntersections(this.layerAtual);
            this.drawing.active.layer = null;
            this.drawing.active.mode = null;
            this.calculateDrawingArea();
        },
        _getLayerError(layer) {
            const error = {
                warning: null,
                fail: null,
            };

            let invalidLayer = false;
            const geom = layer.toGeoJSON(GEOJSON_PRECISION);
            if (geom.geometry.coordinates.length === 0) {
                return error;
            }
            try {
                const intersection = turf.kinks(turf.cleanCoords(geom));
                invalidLayer = intersection.features.length > 0;
            } catch {
                invalidLayer = true;
            }

            if (invalidLayer) {
                error.fail = LayerEventError.INVALID;
                return error;
            }

            if ([OverlapLevel.ERROR, OverlapLevel.WARNING].includes(this.mapOptions.overlapLevel)) {
                const drawedLayers = this._getDrawedLayers();
                for (const drawedLayer of drawedLayers) {
                    if (drawedLayer === this.drawing.active.layer || drawedLayer === layer) {
                        continue;
                    }

                    const intersects = turf.booleanIntersects(geom, drawedLayer.toGeoJSON(GEOJSON_PRECISION));
                    if (intersects) {
                        if (this.mapOptions.overlapLevel === OverlapLevel.ERROR) {
                            error.fail = LayerEventError.INTERSECTS;
                            return error;
                        }
                        error.warning = LayerEventWarning.INTERSECTS;
                    }
                }
            }

            const { imovel } = this.layers.area;
            if (imovel.type === 'FeatureCollection') {
                if (turf.difference(geom, imovel.features[0])) {
                    error.fail = LayerEventError.OUTSIDE_BOUNDARIES;
                    return error;
                }
            }

            return error;
        },
        setLayerColor(layer, color) {
            layer.setStyle({ color });
        },
        _getDrawedLayers() {
            return this.drawing.group.getLayers();
        },
        _paintOverlapedLayers(currentLayer = null) {
            const result = [];
            if (this.mapOptions.overlapLevel === OverlapLevel.NONE) {
                return result;
            }

            const currentGeom = currentLayer?.toGeoJSON(GEOJSON_PRECISION) ?? null;
            if (currentGeom?.geometry.coordinates.length === 0) {
                return result;
            }
            const layers = this._getDrawedLayers();
            const size = layers.length;
            for (let i = 0; i < size; i += 1) {
                const layerA = layers[i];
                this.setLayerColor(layerA, Colors.SUCCESS);

                const geomA = layerA.toGeoJSON(GEOJSON_PRECISION);
                if (currentGeom !== null && currentLayer !== layerA && turf.booleanIntersects(currentGeom, geomA)) {
                    if (!result.includes(currentLayer)) {
                        result.push(currentLayer);
                    }
                    if (!result.includes(layerA)) {
                        result.push(layerA);
                    }
                }
                for (let j = i + 1; j < size; j += 1) {
                    const layerB = layers[j];
                    const geomB = layerB.toGeoJSON(GEOJSON_PRECISION);
                    if (turf.booleanIntersects(geomA, geomB)) {
                        if (!result.includes(layerA)) {
                            result.push(layerA);
                        }

                        if (!result.includes(layerB)) {
                            result.push(layerB);
                        }
                    }
                }
            }

            const color = this.mapOptions.overlapLevel === OverlapLevel.WARNING ? Colors.WARNING : Colors.ERROR;

            for (const layer of result) {
                this.setLayerColor(layer, color);
            }

            return result;
        },
        _paintIntersections(currentLayer) {
            const drawedLayers = this._getDrawedLayers();
            this.layers.intersection.group.remove();
            this.layers.intersection.drawings = [];
            let messageIntersection = null;
            let podeDesenhar = true;
            let newLayer = null;

            this.layers.intersection.group = L.featureGroup([], {
                pane: 'intersectionPane',
            }).addTo(this.map);

            for (const drawing of drawedLayers) {
                let { geometry } = drawing.toGeoJSON(GEOJSON_PRECISION);

                for (const limit of this.layers.area.drawable) {
                    if (limit === geometry) continue;
                    const intersection = turf.intersect(geometry, limit);
                    if (intersection) {
                        if (
                            this.itemMenu.tipo === TipoDocumentoImovelEnum.PROPRIEDADE.id ||
                            this.itemMenu.tipo === TipoDocumentoImovelEnum.POSSE.id
                        ) {
                            if (
                                this.itemMenu.tipo === TipoDocumentoImovelEnum.POSSE.id &&
                                limit.tipo === TipoDocumentoImovelEnum.PROPRIEDADE.id
                            ) {
                                const difference = turf.difference(geometry, intersection);
                                newLayer = L.geoJSON(difference);
                            } else {
                                if (
                                    this.itemMenu.tipo === TipoDocumentoImovelEnum.PROPRIEDADE.id &&
                                    limit.tipo === TipoDocumentoImovelEnum.POSSE.id
                                ) {
                                    messageIntersection =
                                        'Existem áreas de propriedade sobrepostas as áreas de posse. Retire a sobreposição para dar continuidade ao cadastro do ativo ambiental.';
                                }
                                this.layers.intersection.drawings.push(intersection);
                            }
                        }

                        if (
                            this.itemMenu.tipo === TipoDocumentoImovelEnum.SERVIDAO.id ||
                            this.itemMenu.tipo === TipoDocumentoImovelEnum.RPPN.id
                        ) {
                            if (
                                limit.tipo === TipoDocumentoImovelEnum.RPPN.id ||
                                limit.tipo === TipoDocumentoImovelEnum.SERVIDAO.id
                            ) {
                                geometry = turf.difference(geometry, intersection);
                            } else {
                                if (this.areaInvalidaPropPosse) {
                                    geometry = turf.difference(geometry, this.areaInvalidaPropPosse);
                                }
                            }
                            newLayer = L.geoJSON(geometry);
                        }
                    }
                }

                if (this.itemMenu.tipo === TipoDocumentoImovelEnum.RLM.id) {
                    let difference = geometry;
                    if (this.areaInvalidaRLM) {
                        difference = turf.difference(difference, this.areaInvalidaRLM);
                    }
                    if (this.areaInvalidaPropPosse) {
                        difference = turf.difference(difference, this.areaInvalidaPropPosse);
                    }
                    if (difference) {
                        newLayer = L.geoJSON(difference);
                    }
                }

                if (currentLayer) {
                    if (drawing === currentLayer) continue;
                    const { geometry: geomAtual } = currentLayer.toGeoJSON(GEOJSON_PRECISION);
                    const intersection = turf.intersect(geometry, geomAtual);
                    if (intersection) {
                        this.layers.intersection.drawings.push(intersection);
                    }
                }
            }

            let union = null;
            for (const intersection of this.layers.intersection.drawings) {
                if (union === null) {
                    union = intersection;
                } else {
                    union = turf.union(union, intersection);
                }
            }

            if (union !== null) {
                podeDesenhar = false;
                const layer = L.geoJSON(union);
                layer.setStyle({
                    color: Colors.INTERSECTS,
                    fillColor: Colors.INTERSECTS,
                    fillOpacity: 1,
                });
                this.layers.intersection.group.addLayer(layer);
                this._notify(
                    { error: LayerEventError.INTERSECTS, message: messageIntersection },
                    {
                        id: layer._leaflet_id,
                        layer,
                        error: LayerEventError.INTERSECTS,
                    }
                );
            }
            this.$emit('habilitar-desenhar', podeDesenhar);
            return newLayer;
        },
        _notify(event, { error }) {
            let message = null;
            switch (error) {
                case LayerEventError.INVALID:
                    message = 'Geometria inválida.';
                    break;
                case LayerEventError.INTERSECTS:
                    message = event.message ? event.message : 'A geometria possui sobreposição com outra camada.';
                    break;
                case LayerEventError.OUTSIDE_BOUNDARIES:
                    message = 'A geometria está fora da área do imóvel.';
                    break;
                case LayerEventError.INVALID_FILE:
                    message = 'Arquivo enviado é inválido.';
                    break;
                case LayerEventError.EMPTY_FILE:
                    message = 'O arquivo enviado está vazio.';
                    break;
                default:
                    message = 'Não foi possível plotar a geometria no mapa.';
                    break;
            }
            if (event === MapEventError.DRAWING) {
                message = `Problema ao desenhar. ${message}`;
            }

            let notification = this.notifications.shift();
            while (notification) {
                notification.close();
                notification = this.notifications.shift();
            }

            notification = this.$notify({
                title: 'Erro!',
                message,
                type: 'error',
                onClose: () => {
                    const index = this.notifications.indexOf(notification);
                    if (index >= 0) {
                        this.notifications.splice(index, 1);
                    }
                },
            });

            this.notifications.push(notification);
        },
        calculateDrawingArea() {
            let area = 0;
            for (const layer of this.drawing.group.getLayers()) {
                const { geometry } = layer.toGeoJSON(GEOJSON_PRECISION);
                area += turf.area(geometry) / 10e3;
            }
            this.$emit('atualizar-area-vetorizada', area);
            this.areaTotalVetorizada = area;
        },
        getAreaAsText(area, showSuffix = true) {
            const formattedArea = area.toLocaleString('pt-BR', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
            });
            if (showSuffix) {
                return `${formattedArea} ha`;
            }
            return formattedArea;
        },
        _getLayerLatLng(layer) {
            return layer.getLatLngs()[0];
        },
        _layerOnClick(layer) {
            this.$emit('habilitar-desenhar', false);
            this.layerAtual = layer;
            if (this.drawing.active.mode === Mode.DRAWING) {
                return;
            }

            if (this.drawing.active.layer && this.drawing.active.layer !== layer) {
                this.drawing.active.layer.setLatLngs(this.drawing.active.originalLatLng);
                this.drawing.active.layer.disableEdit();
            }

            this.calculateDrawingArea();
            this.drawing.active.mode = Mode.EDITING;
            this.drawing.active.layer = layer;
            this.drawing.active.originalLatLng = this._getLayerLatLng(layer).map(({ lat, lng }) => [lat, lng]);
            layer.enableEdit();
        },
        _layerOnDoubleClick(layer) {
            this.layerAtual = null;
            layer.disableEdit();
            this.drawing.group.removeLayer(layer);

            this.finalizarDesenhoLayer(layer);
        },
        editarVetorizacao(itemMenu) {
            const layersMapa = this.layers.area.group.getLayers();
            for (const layer of layersMapa) {
                if (itemMenu.areas) {
                    for (const area of itemMenu.areas) {
                        if (layer.properties.id === area.id) {
                            this.layers.area.group.removeLayer(layer);
                            let layerEdit = layer.getLayers()[0];
                            layerEdit.setStyle({
                                color: Colors.SUCCESS,
                                fillColor: Colors.SUCCESS,
                                fillOpacity: 0.3,
                            });
                            this.drawing.group.addLayer(layerEdit);
                            layerEdit.on('click', () => this._layerOnClick(layerEdit));
                            layerEdit.on('dblclick', () => this._layerOnDoubleClick(layerEdit));
                        }
                    }
                }
            }

            const areaIds = new Set(itemMenu.areas.map((area) => area.id));

            for (let i = this.layers.area.drawable.length - 1; i >= 0; i--) {
                const drawable = this.layers.area.drawable[i];

                if (areaIds.has(drawable.id)) {
                    this.layers.area.drawable.splice(i, 1);
                }
            }

            for (let i = 0; i < this.grupos.length; i++) {
                const grupo = this.grupos[i];
                if (itemMenu.id === grupo.id) {
                    this.grupos.splice(i, 1);
                }
            }

            this.calculateDrawingArea();
        },
        async salvarPoligono() {
            const intersecoes = this.layers.intersection.group.getLayers();
            this.calculateDrawingArea();
            const possuiDivergencia = this.verificarDivergencia(this.areaTotalVetorizada);
            if (this.drawing.active.layer) {
                this.$notify({
                    title: 'Atenção!',
                    message: 'Por favor, finalize a vetorização antes de salvar',
                    showClose: true,
                    type: 'warning',
                });
                return;
            }
            if (intersecoes.length === 0 && !possuiDivergencia) {
                const areasDesenhadas = this.drawing.group.getLayers();
                let areas = [];
                let color = null;
                if (this.itemMenu.areas.length > 0) {
                    color = this.itemMenu?.areas[0].color;
                } else {
                    color = this.gerarCorCamada();
                }

                for (const layer of areasDesenhadas) {
                    const geoJSON = layer.toGeoJSON(GEOJSON_PRECISION);
                    const area = {
                        color,
                        geo: this.wkt.fromObject(geoJSON.geometry).write(),
                        area: turf.area(geoJSON.geometry) / 10e3,
                        idDocumentoImovel: this.itemMenu.id,
                    };

                    areas.push(area);
                }

                try {
                    const tipo = Object.values(TipoDocumentoImovelEnum).find((item) => item.id === this.itemMenu.tipo);
                    const response = await salvarGeometria({
                        areas,
                        id: this.itemMenu.id,
                        tipo: tipo.codigo,
                    });
                    this.itemMenu.areas = response ?? [];
                    this.itemMenu.hasGeo = this.itemMenu.areas.length > 0;
                    this.$emit('close-menu');

                    this.$notify({
                        title: 'Sucesso!',
                        message: 'As vetorizações foram salvas com sucesso!',
                        showClose: true,
                        type: 'success',
                    });
                } catch (e) {
                    this.$notify({
                        title: 'Erro!',
                        message: 'Ocorreu um erro ao tentar salvar geometria.',
                        showClose: true,
                        type: 'error',
                    });
                }
            } else {
                let message = '';
                if (possuiDivergencia) {
                    message += 'A área vetorizada está fora do limite de tolerância. ';
                }
                if (intersecoes.length > 0) {
                    message += 'Por favor, remova as interseções antes de salvar. ';
                }
                if (this.areaInvalidaPropPosse) {
                    message += 'A área deve ser vetorizada apenas em área de propriedade ou posse. ';
                }
                this.$notify({
                    title: 'Atenção!',
                    message,
                    showClose: true,
                    type: 'warning',
                });
            }
        },
        verificarDivergencia(areaVetorizada) {
            const divergenciaMaximaPermitida = this.modulosFiscais <= 4 ? 100 : 50;
            const percentual = 1 + divergenciaMaximaPermitida / 100;
            const areaDeclarada = this.itemMenu.area;
            return areaVetorizada > percentual * areaDeclarada || areaDeclarada > percentual * areaVetorizada;
        },
        gerarCorCamada() {
            return `#${Math.floor(Math.random() * 16777215)
                .toString(16)
                .padStart(6, '0')}`;
        },
        fecharMenu() {
            this.drawing.active.mode = null;
            this.drawing.active.layer = null;
            this.drawing.active.originalLatLng = null;
            this.areaInvalidaPropPosse = null;
            this.areaInvalidaRLM = null;

            for (const layer of this.drawing.group.getLayers()) {
                this.drawing.group.removeLayer(layer);
            }
            this.layers.intersection.group.remove();

            if (this.itemMenu.areas.length > 0) {
                this.adicionarCamadasMapa([this.itemMenu]);
            }
        },
        uploadFile(file) {
            for (const geo of file.features) {
                const layer = L.geoJSON(geo);
                this.finalizarDesenhoLayer(layer.getLayers()[0]);
            }

            const { raw, name, type } = file;
            if (!type) {
                this._notify(MapEventError.UPLOAD, {
                    id: null,
                    name: null,
                    layer: null,
                    error: LayerEventError.INVALID_FILE,
                });
                this.modal.upload = false;
                return;
            }

            const fr = new FileReader();

            let parser;
            switch (type) {
                case UploadableFileTypes.KML:
                    parser = async (result) => {
                        const parsing = omnivore.kml.parse(result.replace(/<(document).*?>/gim, '<$1>'));
                        return parsing.getLayers();
                    };
                    break;
                case UploadableFileTypes.SHAPE:
                    parser = async (result) => {
                        try {
                            const fn = shp.default || shp;
                            const data = await fn(result);
                            return L.geoJSON(data).getLayers();
                        } catch (e) {
                            console.log(e);
                            return null;
                        }
                    };
                    break;
            }

            const self = this;

            fr.onerror = () => {
                self._notify(MapEventError.UPLOAD, {
                    id: null,
                    name: null,
                    layer: null,
                    error: LayerEventError.INVALID_FILE,
                });
            };

            fr.onload = async () => {
                const parsedLayers = await parser(fr.result);
                if (parsedLayers === null) {
                    self._notify(MapEventError.UPLOAD, {
                        id: null,
                        name: null,
                        layer: null,
                        error: LayerEventError.INVALID_FILE,
                    });
                    return;
                }

                const layers = [];
                parsedLayers.forEach((layer) => {
                    const geoJSON = layer.toGeoJSON(GEOJSON_PRECISION);
                    const { geometry } = geoJSON;
                    if (geometry.type === 'MultiPolygon') {
                        const latlngs = layer.getLatLngs();
                        for (const latlng of latlngs) {
                            const polygon = L.polygon(latlng);
                            layers.push(polygon);
                        }
                        layer.remove();
                    } else if (geometry.type === 'GeometryCollection' && Array.isArray(geometry.geometries)) {
                        for (const geom of geometry.geometries) {
                            const polygon = L.geoJSON(geom);
                            layers.push(...polygon.getLayers());
                        }
                        layer.remove();
                    } else {
                        layers.push(layer);
                    }
                });

                const size = layers.length;
                if (size === 0) {
                    self._notify(MapEventError.UPLOAD, {
                        id: null,
                        name: null,
                        layer: null,
                        error: LayerEventError.EMPTY_FILE,
                    });
                    return;
                }

                layers.forEach((layer, index) => {
                    const geoJSON = layer.toGeoJSON(GEOJSON_PRECISION);
                    const { geometry } = geoJSON;

                    if (geometry.type === 'LineString') {
                        return;
                    } else {
                        self.drawType = 'POLYGON';
                        self.geometryFunction = L.polygon;
                    }

                    layer.addTo(self.map);

                    const error = self._getLayerError(layer);
                    if (error.fail !== null) {
                        self._notify(MapEventError.UPLOAD, {
                            id: null,
                            layer,
                            error: error.fail,
                        });
                        layer.remove();
                        if (index === size - 1) {
                            self.focusOnCenter();
                        }
                        return;
                    }

                    if (error.warning !== null) {
                        self._notify(MapEventError.UPLOAD, {
                            id: null,
                            layer,
                            error: error.warning,
                        });
                    }

                    const drawedLayer = self.addDrawingFromLayer(layer);
                    const leafletId = drawedLayer.id;

                    self.$emit(MapEventSuccess.UPLOADED, {
                        id: leafletId,
                        geometry: geoJSON.geometry,
                    });

                    layer.remove();
                    if (index === size - 1) {
                        self.focusOnCenter();
                    }
                });
                self._paintOverlapedLayers();
            };

            switch (type) {
                case UploadableFileTypes.KML:
                    fr.readAsText(raw);
                    break;
                case UploadableFileTypes.SHAPE:
                    fr.readAsArrayBuffer(raw);
                    break;
            }
        },
    },
};
</script>

<style scoped lang="scss">
#map {
    position: relative;
    display: flex;
    overflow: hidden;
    width: 100%;
    height: 400px;
}

.side-icon-container {
    position: absolute;
    top: 80px;
    right: 10px;
    z-index: 1000;
}

.side-icon {
    background-color: #fff;
    background: white;
    font-size: 20px;
    border-radius: 2px;
    line-height: 1;
    padding: 5px;
    cursor: pointer;

    div {
        display: inline-block;
    }

    &.active {
        background-color: #3ab985;
        color: #fff !important;
    }

    &:hover:not(.active) {
        background-color: #eee;
    }

    &:not(:last-child) {
        margin-bottom: 30px;
    }
}

.focus {
    margin-top: 38px;
}
</style>
